import { faCloudArrowUp } from "@fortawesome/pro-duotone-svg-icons"
import { faImage } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Link, Tooltip, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { getFormattedFileSize } from "app/tools/file"
import React from "react"
import { FileDrop } from "react-file-drop"
import { FlexBox } from "ui/components/FlexBox"
import { Popup } from "ui/components/Popup"
import { UploadProgressText } from "ui/components/UploadProgressText"
import { IUiConfigUploadData, MAX_UI_CONFIG_FILE_UPLOAD_SIZE_IN_BYTES } from "../model"
import { getDefaultUiConfigUploadState, isUiConfigUploadFileValid, uploadUiConfigFile } from "../tools/upload"

interface IUiConfigUploadPopupProps {
  title: string
  isIco: boolean
  onClose: () => any
  onUploadSuccess: (url: string) => any
}

export const UiConfigUploadPopup = React.memo(
  ({ title, isIco, onClose, onUploadSuccess }: IUiConfigUploadPopupProps): JSX.Element => {
    const [data, setData] = React.useState<IUiConfigUploadData>(getDefaultUiConfigUploadState())
    const setUploadData = (partialData: Partial<IUiConfigUploadData>): void => setData({ ...data, ...partialData })
    const { isUploading, progressPercent, selectedFiles, error } = data
    const onFileSelection = (files: FileList | null) => {
      if (isUploading) {
        return
      }
      if (error) {
        setUploadData({ error: "" })
      }
      if (isUiConfigUploadFileValid(files, isIco)) {
        setUploadData({
          selectedFiles: files,
          error: ""
        })
      }
    }
    return (
      <Popup
        icon={faImage}
        id="ui-config-upload-popup"
        displayCloseIcon={!isUploading}
        minWidth="40rem"
        maxWidth="40rem"
        title={title}
        onClose={origin => {
          if (origin === "outside-click") {
            return
          }
          if (!isUploading) {
            onClose()
          }
        }}>
        <FlexBox jc="space-between" ai="flex-start" sx={{ margin: "1rem" }}>
          <FlexBox
            sx={{
              border: theme => `1px dashed ${theme.palette.text.secondary}`,
              backgroundColor: theme => theme.palette.background.paper,
              width: "50%",
              minWidth: "50%",
              maxWidth: "100%",
              height: "100%",
              minHeight: "14rem",
              maxHeight: "14rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: theme => theme.palette.text.primary2
            }}
            onClick={() => document.getElementById("ui-config-upload-input")?.click()}>
            <FileDrop onDrop={files => onFileSelection(files)} className="file-drop">
              <FlexBox
                vertical
                sx={{
                  "margin": "1rem",
                  "& > *": {
                    maxWidth: "19rem"
                  }
                }}>
                <FontAwesomeIcon icon={faCloudArrowUp} className="drop-icon" size="2x" opacity=".5" />
                <Typography fontSize="1rem" textAlign="center">
                  {translate("label.drop.file")}
                </Typography>
                <FlexBox gap=".3rem">
                  <Typography fontSize="1rem">{translate("label.or")}</Typography>
                  <div>
                    <input
                      id="ui-config-upload-input"
                      style={{ display: "none" }}
                      type="file"
                      onChange={event => onFileSelection(event.target.files)}
                    />
                    <Link component="button" variant="body2" fontSize="1rem" disabled={isUploading}>
                      {translate("browse")}
                    </Link>
                  </div>
                </FlexBox>
                <Typography color="textSecondary" fontSize=".8rem" textAlign="center">
                  {translate("label.file.max.size", getFormattedFileSize(MAX_UI_CONFIG_FILE_UPLOAD_SIZE_IN_BYTES, 0))}
                </Typography>
              </FlexBox>
            </FileDrop>
          </FlexBox>
          <FlexBox
            ai="start"
            vertical
            fullWidth
            gap
            sx={{ pl: "1rem", pr: "1rem", width: "50%", minWidth: "50%", maxWidth: "100%" }}>
            {isUploading && progressPercent < 100 ? (
              <UploadProgressText
                primaryText={translate("upload.in.progress")}
                secondaryText={`${Math.round(progressPercent)}%`}
              />
            ) : isUploading ? (
              <UploadProgressText
                primaryText={translate("upload.processing")}
                secondaryText={translate("label.in.progress")}
              />
            ) : selectedFiles && selectedFiles.length > 0 ? (
              <FlexBox ai="flex-start" vertical fullWidth gap>
                <FlexBox fullWidth jc="space-between">
                  <Typography color="textSecondary" fontSize="0.8rem">
                    {translate("label.selected.file")}
                  </Typography>
                  <Typography
                    fontSize=".8rem"
                    sx={{
                      color: theme => theme.palette.text.disabled
                    }}>
                    {getFormattedFileSize(selectedFiles[0].size)}
                  </Typography>
                </FlexBox>
                <Tooltip title={selectedFiles[0].name}>
                  <Typography
                    fontSize=".8rem"
                    sx={{
                      width: "100%",
                      color: theme => theme.palette.text.primary2
                    }}>
                    {selectedFiles[0].name}
                  </Typography>
                </Tooltip>
              </FlexBox>
            ) : (
              <Typography color="textSecondary" fontSize=".8rem" fontStyle="italic">
                {translate("upload.no.file")}
              </Typography>
            )}
            {error && (
              <Typography color="error" fontSize=".9rem">
                {error}
              </Typography>
            )}
          </FlexBox>
        </FlexBox>
        <FlexBox jc="flex-end" ai="flex-start" fullWidth sx={{ padding: "1rem" }}>
          <Button
            variant="contained"
            onClick={() => uploadUiConfigFile(data, setUploadData).then(url => onUploadSuccess(url))}
            disabled={isUploading || !selectedFiles || selectedFiles.length === 0 || error !== ""}>
            <Typography fontWeight="bold" fontSize="small">
              {translate("button.upload")}
            </Typography>
          </Button>
        </FlexBox>
      </Popup>
    )
  }
)
